import { type ReactNode } from 'react';

import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { APP_HEADER_H } from '@/karehero-app/shell/layout';

const SCREEN_PX = 3; // 24px gutter

export type AppBarProps = {
  /** Title shown in the bar. Omit for a plain "Back" bar (the page then
   *  usually renders its own PageHeader as the first content child). */
  title?: string;
  /** Small line centred under the title. Needs `title`. */
  subtitle?: string;
  /** Back control: a chevron beside the title, or a "Back" text button when
   *  there's no title. Omit for a bar with no back affordance. */
  onBack?: () => void;
  /** Text on the no-title back button. Defaults to "Back"; use e.g. "Exit"
   *  when the control leaves a flow rather than stepping back within it. */
  backLabel?: string;
  /** Optional trailing action, pinned right. Keep it to one icon or short
   *  label so the centred title stays balanced. */
  action?: ReactNode;
  /**
   * A control of the bar's own in place of the back chevron, pinned left.
   * Centred bars only.
   */
  leading?: ReactNode;
  /** Hairline under the bar. Default true. */
  divider?: boolean;
  /**
   * Where the title sits. 'center' is the pushed-page default (back chevron
   * pinned left, title optically centred). 'left' lays the bar out like
   * Navigation — title against the gutter where the logo sits, action against
   * the gutter where the avatar sits — for a flow that is somewhere you are
   * rather than a page you pushed.
   */
  align?: 'center' | 'left';
  /**
   * How loud the title is. 'default' is h4, which titles a page.
   *
   * 'small' is subtitle2, for a left-aligned bar whose title is a
   * sentence rather than a name — "Tell us about the person you're caring for"
   * at h4 wraps to two lines and turns the header into a paragraph. It is the
   * size that text was before it moved into the bar. Centred, it swaps the h2
   * for h6, 16px medium.
   */
  size?: 'default' | 'small';
};

// The contextual app bar for pushed pages and flows: a back chevron with a
// centred title (and optional subtitle + trailing action), or a plain "Back"
// bar when there's no title. The counterpart to Navigation — Navigation is the
// persistent root-page header (logo + name + bell + avatar), AppBar is the
// "where am I, how do I get back" bar for everything pushed on top of it. One
// component so every pushed header is identical (and the same APP_HEADER_H, so
// the bar doesn't jump when you push from a root page into a sub-page).
const AppBar = ({
  title,
  subtitle,
  onBack,
  leading,
  action,
  backLabel = 'Back',
  divider = true,
  align = 'center',
  size = 'default',
}: AppBarProps) => (
  <Box sx={{ flexShrink: 0 }}>
    {title && align === 'left' ? (
      // Navigation's geometry, so a flow bar and a root bar line up: one
      // gutter each side, the same header height, nothing pinned absolutely.
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: SCREEN_PX,
          minHeight: APP_HEADER_H,
        }}
        direction='row'
        spacing={2}
      >
        <Stack sx={{ alignItems: 'center', minWidth: 0 }} direction='row'>
          {onBack && (
            <Box sx={{ ml: -1, mr: 0.5 }}>
              <IconButton onClick={onBack} aria-label='Go back' size='small'>
                <Icon icon='chevron-left' fontSize={18} />
              </IconButton>
            </Box>
          )}
          <Stack sx={{ minWidth: 0 }} spacing={0}>
            {/* h4 in secondary text: a left-aligned bar sits directly above
                the page's own PageHeader, so an h2 in full contrast here would
                be the same heading twice. The bar says where you are; the
                heading under it says what to do. In sx, not Typography's color
                prop — llama bakes a literal colour into every variant at import
                time, so the prop is inert. */}
            {/* The small size wraps rather than truncating: it is carrying a
                sentence, and clipping "Tell us about the person you're caring
                for" to "Tell us about the person ..." is worse than a second
                line. A default-size title is a name, so it stays on one. */}
            {/* Marine green, the logo's own colour. This title sits where the
                logo sits — that is the whole point of the left align — so it
                is standing in for the mark rather than labelling the page, and
                a grey line there read as a caption the bar had been given. */}
            <Typography
              sx={{ color: 'primary.main' }}
              variant={size === 'small' ? 'subtitle2' : 'h4'}
              noWrap={size !== 'small'}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary' }}
                noWrap
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        </Stack>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Stack>
    ) : title ? (
      // Centred nav-bar title: back pinned left, title centred, action pinned
      // right, so the title stays centred whatever flanks it.
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: SCREEN_PX,
          minHeight: APP_HEADER_H,
        }}
      >
        {leading ? (
          <Box sx={{ position: 'absolute', left: 16 }}>{leading}</Box>
        ) : (
          onBack && (
            <Box sx={{ position: 'absolute', left: 0.5 }}>
              <IconButton onClick={onBack} aria-label='Go back' size='small'>
                <Icon icon='chevron-left' fontSize={18} />
              </IconButton>
            </Box>
          )
        )}
        <Stack sx={{ alignItems: 'center', minWidth: 0 }} spacing={0}>
          {/* Small, a flow's centred title: 16px medium, the chat's own size
              a little heavier, so it never reads smaller than what is under
              it. */}
          <Typography
            variant={size === 'small' ? 'h6' : 'h2'}
            sx={size === 'small' ? { color: 'primary.main' } : undefined}
            noWrap
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {action && <Box sx={{ position: 'absolute', right: 1 }}>{action}</Box>}
      </Box>
    ) : (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: SCREEN_PX,
          minHeight: APP_HEADER_H,
        }}
        direction='row'
      >
        <Box>
          {onBack && (
            <Button
              variant='link'
              ariaLabel={backLabel}
              iconLeft='chevron-left'
              onPress={onBack}
            >
              {backLabel}
            </Button>
          )}
        </Box>
        {action}
      </Stack>
    )}
    {divider && <Divider />}
  </Box>
);

AppBar.displayName = 'AppBar';
export default AppBar;
